/*
 * Copyright (C) 2018 Frontier Developments PLC.
 */
import {Vue} from 'vue-property-decorator';
import {MutationTree} from 'vuex';
import {UgcBuildStateInterface, UgcRevisionInterface} from '@/store/modules/ugc-build/types';
import {UgcItemInterface} from '@/store/modules/ugc-item/types';
import {UgcNotificationItemInterface} from '@/store/modules/ugc-notification/types';

/**
 * Ugc item mutations.
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */

export const mutations: MutationTree<UgcBuildStateInterface> = {
    /**
     * Add build items set to state.
     *
     * @param {UgcBuildStateInterface} state
     * @param {UgcItemInterface} item
     */
    addItem(state: UgcBuildStateInterface, item: UgcItemInterface): void {
        Vue.set(state, 'item', item);
    },

    /**
     * Apply notifications to revision items.
     *
     * @param {UgcBuildStateInterface} state
     * @param {UgcNotificationItemInterface[]} items
     */
    applyNotifications(state: UgcBuildStateInterface, items: UgcNotificationItemInterface[]): void {
        for (const item of items) {
            if (state.item && item.itemId === state.item.id && state.item.updatedAt < item.updatedAt) {
                state.item.status = item.status;
                state.item.updatedAt = item.updatedAt;
            }

            const revisionItem: UgcRevisionInterface | undefined =
                state.revisions.find((revision: UgcRevisionInterface) => revision.id === item.buildId);

            if (undefined === revisionItem || revisionItem.updatedAt > item.updatedAt) {
                continue;
            }

            revisionItem.status = item.status;
            revisionItem.updatedAt = item.updatedAt;
        }
    },

    /**
     * Add revision items set to state.
     *
     * @param {UgcBuildStateInterface} state
     * @param {UgcRevisionInterface[]} items
     */
    addRevisionItems(state: UgcBuildStateInterface, items: UgcRevisionInterface[]): void {
        Vue.set(state, 'revisions', items);
    },

    /**
     * Delete revision item from revisions.
     *
     * @param {UgcBuildStateInterface} state
     * @param {UgcRevisionInterface} revision
     */
    deleteRevisionItem(state: UgcBuildStateInterface, revision: UgcRevisionInterface): void {
        const index: number = state.revisions.findIndex(
            (item: UgcRevisionInterface) => item.id === revision.id,
        );

        if (index >= 0) {
            state.revisions.splice(index, 1);
        }
    },

    /**
     * Add revision to build set.
     *
     * @param {UgcBuildStateInterface} state
     * @param {UgcRevisionInterface} build
     */
    addRevisionItem(state: UgcBuildStateInterface, build: UgcRevisionInterface): void {
        // grab address id
        // check to see if item already exists and remove
        const index: number = state.revisions.findIndex((revision: UgcRevisionInterface) => revision.id === build.id);

        if (index >= 0) {
            state.revisions.splice(index, 1);
        }

        // add returned item
        state.revisions.push(build);
    },

};
